import { formatSocials } from "./../../utils/profile";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useWallet } from "../../services/wallet/useWallet";
import { isValidEnsString } from "../../utils/addressUtils";
import {
  createHighlightedKudos,
  deleteHighlightedKudos,
  getClaimableKudos,
  getIdentityByAddress,
  getReceivedKudos,
  getSentKudos,
  getUserIdentity,
  postBurnKudos,
} from "./../../services/apis/ProfileApi/KudosProfileApi";
import useKudosSignature from "../../hooks/useKudosSignature";

export enum ProfileTab {
  Highlights = 0,
  Received = 1,
  Claimable = 2,
  Sent = 3,
}

export const useProfile = (query: string) => {
  const [username, setUsername] = useState("");

  const [selectedIndex, setSelectedIndex] = useState(ProfileTab.Received);
  const { getKudosSignature } = useKudosSignature();
  const { addresses } = useWallet();

  const router = useRouter();
  const searchParams = useMemo(() => {
    return router.query;
  }, [router.query]);
  const setSearchParams = (params: any) => {
    const routeQuery = {
      username: query,
      tab: params.tab,
      tokenId: params.tokenId,
    };

    if (!params.tokenId) {
      delete routeQuery.tokenId;
    }

    router.push({
      pathname: router.pathname,
      query: routeQuery,
    });
  };

  const tab = (searchParams?.tab as string) ?? "";

  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [highlighted, setHighlighted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socials, setSocials] = useState([]);
  const [error, setError] = useState<boolean | null>(false);

  const changeIndex = async (index: ProfileTab) => {
    setSelectedIndex(index);
    setSearchParams({ ...searchParams, tab: ProfileTab[index] });
  };

  useEffect(() => {
    if (searchParams.tab) {
      const tabValue: ProfileTab =
        ProfileTab[searchParams.tab as keyof typeof ProfileTab];
      if (tabValue !== selectedIndex) {
        setSelectedIndex(tabValue);
      }
    }
  }, [searchParams]);

  const wallet = useWallet();
  const { claimable } = wallet;

  const isMe = useMemo(() => {
    return (
      wallet.username === query ||
      (isValidEnsString(wallet.displayName) && wallet.displayName === query)
    );
  }, [query, wallet.username, wallet.displayName]);

  useEffect(() => {
    const fetchClaimableKudos = async () => {
      if (!username) {
        return;
      }

      if (tab === "Claimable" && !isMe) {
        changeIndex(0);
        return;
      }

      if (isMe) {
        const claimResponse = await wallet.fetchClaimable(username);

        // Redirect to first tab if direct link to Claimable with no claimable kudoses
        if (!claimResponse?.data?.length && tab === "Claimable") {
          changeIndex(0);
        }
      }
    };
    fetchClaimableKudos();
  }, [isMe, username]);

  const hasMaxHighlights: boolean = useMemo(
    () => highlighted?.length >= maxHighlights,
    [highlighted],
  );

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);

        if (!query || error) {
          return;
        }
        setError(null);

        let identity;
        try {
          identity = await getUserIdentity(query);
        } catch (err) {
          // err here means that identity could not be fetched
          const result: any = await getIdentityByAddress(query);
          identity = result.data?.length > 0 && result.data[0];
        }
        const fetchedUsername = identity?.username || "";
        setUsername(fetchedUsername);

        if (!fetchedUsername) {
          setLoading(false);
          setError(true);
          return;
        } else if (fetchedUsername !== query) {
          router.push({
            pathname: `/profile/${fetchedUsername}`,
            query: searchParams,
          });
          return;
        }

        const received: any = await getReceivedKudos(fetchedUsername);
        setReceived(received?.data);
        const sent: any = await getSentKudos(fetchedUsername);
        setSent(sent?.data);
        setHighlighted(identity?.highlightedKudoses);
        const socials: any = formatSocials(identity);
        setSocials(socials);

        if (
          !tab ||
          !Object.values(ProfileTab).includes(tab) ||
          (tab === "Highlights" && !identity?.highlightedKudoses?.length) ||
          (tab === "Received" && !received?.data?.length) ||
          (tab === "Sent" && !sent?.data?.length)
        ) {
          let selectedTab;
          if (identity.highlightedKudoses.length > 0) {
            selectedTab = ProfileTab.Highlights;
          } else if (received?.data?.length > 0) {
            selectedTab = ProfileTab.Received;
          } else if (claimable?.data?.length > 0) {
            selectedTab = ProfileTab.Claimable;
          } else if (sent?.data?.length > 0) {
            selectedTab = ProfileTab.Sent;
          } else {
            selectedTab = ProfileTab.Received;
          }
          changeIndex(selectedTab);
        } else if (tab !== "Claimable") {
          const tabValue: ProfileTab =
            ProfileTab[tab as keyof typeof ProfileTab];
          changeIndex(tabValue);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setError(true);
      }
    };

    fetchProfile();
  }, [query]);

  const highlightKudos = async (tokenId: string) => {
    if (!hasMaxHighlights) {
      await createHighlightedKudos(tokenId);
      await updateHighlights();
      toast.success("Added kudos to highlights");
    }
  };

  const updateHighlights = async () => {
    const res = await getUserIdentity(username);
    setHighlighted(res?.highlightedKudoses);
  };

  const removeHighlight = async (tokenId: string) => {
    await deleteHighlightedKudos(tokenId);
    setHighlighted(
      highlighted.filter((token: any) => token?.tokenId !== tokenId),
    );
    toast.success("Removed kudos from highlights");
    if (highlighted.length === 1) {
      changeIndex(ProfileTab.Received);
    }
  };

  const burnKudos = async (tokenId: string) => {
    try {
      const types = {
        Burn: [{ name: "tokenId", type: "uint256" }],
      };
      const signature = await getKudosSignature(types, {
        tokenId: Number(tokenId),
      });
      await postBurnKudos(tokenId, {
        burningAddress: addresses[0].address,
        signature,
      });
      await updateReceivedKudos();
      toast.success("Burned Kudos");
    } catch (error: any) {
      // When a signature is denied, sometimes wagmi will throw a non human-readable
      // error as a list of numbers (ex. 87,101,108,99...). This catches that case using
      // the "ACTION_REJECTED" code that's returned and displays the correct message that
      // should be shown from a 4001 (UserDeniedSignatureError).
      if (error.code?.length > 0 && error.code.includes("ACTION_REJECTED")) {
        toast.error("User rejected request");
      } else {
        toast.error(error.message);
      }
    }
  };

  const updateReceivedKudos = async () => {
    const receivedRawData: any = await getReceivedKudos(username);
    setReceived(receivedRawData?.data);

    // the value below is 1 instead of 0 because the state variable isn't immediately
    // updated before a new render
    if (received.length === 1) {
      changeIndex(ProfileTab.Claimable);
    }
  };

  const hasNoTokens =
    !loading &&
    !error &&
    !received?.length &&
    !sent?.length &&
    !highlighted?.length &&
    !claimable?.length;

  return {
    selectedIndex,
    changeIndex,
    ProfileTab,
    received,
    sent,
    claimable,
    highlighted,
    isMe,
    error,
    loading,
    highlightKudos,
    removeHighlight,
    burnKudos,
    socials,
    hasNoTokens,
    tab,
    hasMaxHighlights,
    username,
  };
};

const maxHighlights: number = 4;
